import React from 'react';
import styles from './editContactModal.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import restFactory from 'v2/utils/restFactory';
import { Contact, ContactFormFields, ContactFormProps, User } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import ContactDescriptionForm from './Forms/ContactDescriptionForm';
import ContactAddressForm from './Forms/ContactAddressForm';
import ContactPhoneNumberForm from './Forms/ContactPhoneNumberForm';
import ContactLinkedinForm from './Forms/ContactLinkedinForm';
import ContactOwnerForm from './Forms/ContactOwnerForm';
import ContactProfileForm from './Forms/ContactProfileForm';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';

export enum EditionTypesText {
  description = 'description',
  address = 'address',
  phone_number = 'phone number',
  linkedin = 'linkedin',
  contact_owner = 'owner',
  profile = 'profile',
}

interface EditContactModalProps {
  editionType: EditionTypesText;
  title: string;
  show: boolean;
  contact: Contact;
  contactOwner?: User;
  onClose: (contact?: Contact) => void;
}

const formType: {
  [key in EditionTypesText]: {
    component: (props: ContactFormProps) => JSX.Element;
    fields: string[];
  };
} = {
  [EditionTypesText.description]: {
    component: ContactDescriptionForm,
    fields: ['description'],
  },
  [EditionTypesText.address]: {
    component: ContactAddressForm,
    fields: ['address'],
  },
  [EditionTypesText.phone_number]: {
    component: ContactPhoneNumberForm,
    fields: ['phone_number'],
  },
  [EditionTypesText.linkedin]: {
    component: ContactLinkedinForm,
    fields: ['linkedin'],
  },
  [EditionTypesText.contact_owner]: {
    component: ContactOwnerForm,
    fields: ['contact_owner'],
  },
  [EditionTypesText.profile]: {
    component: ContactProfileForm,
    fields: [
      'first_name',
      'last_name',
      'company_name',
      'title',
      'avatar_url',
      'internal_designation',
    ],
  },
};

const EditContactModal = ({
  show,
  contact,
  contactOwner,
  onClose,
  editionType,
  title,
}: EditContactModalProps) => {
  const { t } = useTranslation();
  const formContext = useForm<ContactFormFields>({
    mode: 'onChange',
    defaultValues: {
      contact: contact.id,
      ..._.pick(contact, formType[editionType].fields),
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
    setValue,
    getValues,
    control: { _fields },
  } = formContext;

  const { isLoading: isContactMutateLoading, mutate: contactMutate } =
    useMutation(
      async (data: ContactFormFields) => {
        return await restFactory.patch<Contact>(
          `/dj/api/v1/contact/${contact?.id}/`,
          data
        );
      },
      {
        onSuccess: () => {
          toast.success(
            `${t(TranslationKeys.contact_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} ${editionType} saved.`
          );
          onClose();
        },
        onError: () => {
          toast.error(
            `Error saving ${t(TranslationKeys.contact_one)} ${editionType}.`
          );
        },
      }
    );

  const onSubmit = async (data: ContactFormFields) => {
    if (isValid) {
      contactMutate(data);
    }
  };

  const FormTypeInstance = formType[editionType].component;

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={true}
      show={show}
      onCancel={() => onClose()}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      isLoading={isContactMutateLoading}
      title={title}
      data-testid="contact-edit-modal"
    >
      <FormProvider {...formContext}>
        <FormTypeInstance
          contact={contact}
          register={register}
          setValue={setValue}
          contactOwner={contactOwner}
          isValid={isValid}
          getValues={getValues}
          fieldsRef={_fields}
        />
      </FormProvider>
    </ConfirmationModal>
  );
};

export default EditContactModal;
